import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, throwError, of } from 'rxjs';
import { tap, catchError, takeUntil, first, switchMap } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { API_BASE_URL } from '../config/app-settings.tokens';
import { AuthService } from './auth.service';
import {
  NotificationEvent,
  NotificationsResponse,
  NotificationSingleResponse,
  MarkAllAsReadResponse,
  DeleteNotificationResponse,
  UnreadCountResponse,
  NotificationEnumConverter,
  ResourceType,
  NotificationAction,
  NotificationType
} from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiUrl = `${inject(API_BASE_URL)}`;
  private hubUrl = `${inject(API_BASE_URL)}/hubs/notifications`;
  private hubConnection: signalR.HubConnection | null = null;
  private isConnected = false;
  private currentUserId: string | null = null;
  private userEmail: string | null = null;

  // Subject to notify when user ID is initialized
  private userInitializedSubject = new BehaviorSubject<boolean>(false);
  private userInitialized$ = this.userInitializedSubject.asObservable();

  // Subjects for notifications
  private notificationsSubject = new BehaviorSubject<NotificationEvent[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Initialize SignalR connection
   * Called from app component or navbar when user is authenticated
   * Backend automatically adds connection to group in OnConnectedAsync
   */
  public initializeConnection(email: string): void {
    if (this.isConnected) {
      return;
    }

    this.userEmail = email;

    // First, fetch user details by email to get iamUserId
    this.authService.getUserByEmail(email).subscribe({
      next: (response: any) => {
        // Extract iamUserId from response
        const iamUserId = response?.data?.iamUserId || response?.iamUserId;
        if (!iamUserId) {
          console.error('No iamUserId found in user response');
          this.userInitializedSubject.next(false);
          return;
        }

        this.currentUserId = iamUserId.toString();
        this.userInitializedSubject.next(true);
        this.setupSignalRConnection(iamUserId.toString());
      },
      error: (err) => {
        console.error('Failed to fetch user details for notifications:', err);
        this.userInitializedSubject.next(false);
      }
    });
  }

  /**
   * Ensure user is initialized by fetching iamUserId from email
   * This is a global solution to ensure all API calls have the correct user ID
   * Public method for use in other services
   */
  public ensureUserInitialized(): Observable<string> {
    // If user is already initialized, return the ID
    if (this.currentUserId) {
      return of(this.currentUserId);
    }

    // If user email is set but ID not initialized, fetch it
    if (this.userEmail && !this.currentUserId) {
      return this.authService.getUserByEmail(this.userEmail).pipe(
        switchMap((response: any) => {
          const iamUserId = response?.data?.iamUserId || response?.iamUserId;
          if (!iamUserId) {
            return throwError(() => ({
              message: 'No iamUserId found in user response',
              error: null
            }));
          }
          this.currentUserId = iamUserId.toString();
          this.userInitializedSubject.next(true);
          return new Observable<string>(observer => {
            observer.next(iamUserId.toString());
            observer.complete();
          });
        }),
        catchError(error => {
          console.error('Error ensuring user initialization:', error);
          return throwError(() => ({
            message: 'Failed to fetch user details',
            error
          }));
        })
      );
    }

    // If no email is set, try to get from auth state
    const authState = this.authService.getAuthState();
    if (authState.currentUser?.email) {
      this.userEmail = authState.currentUser.email;
      return this.ensureUserInitialized();
    }

    // Cannot initialize user
    return throwError(() => ({
      message: 'No user email available for initialization',
      error: null
    }));
  }

  /**
   * Setup SignalR connection (internal method)
   */
  private setupSignalRConnection(userId: string): void {
    try {
      // SignalR hub URL - must match backend endpoint
      // The backend hub is at: /externalbackend/hubs/notifications
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => {
            const token = this.authService.getAccessToken();
            return token || '';
          },
          // Use only Long Polling - WebSocket doesn't work in Docker environment
        //transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          transport: signalR.HttpTransportType.LongPolling,
          withCredentials: false
        })
        .configureLogging(signalR.LogLevel.Information) // Reduce verbosity - use Information level
        .withAutomaticReconnect([0, 2000, 5000, 10000]) // Retry with backoff
        .withServerTimeout(30000)
        .build();

      // Handle connection events
      this.hubConnection.onreconnecting(() => {
        this.connectionStatusSubject.next(false);
      });

      this.hubConnection.onreconnected(() => {
        this.connectionStatusSubject.next(true);
        // Re-subscribe to notifications
        this.subscribeToNotifications(userId);
      });

      this.hubConnection.onclose(() => {
        this.connectionStatusSubject.next(false);
        this.isConnected = false;
      });

      // Start connection
      this.hubConnection
        .start()
        .then(() => {
          this.isConnected = true;
          this.connectionStatusSubject.next(true);

          // Subscribe to notifications
          this.subscribeToNotifications(userId);

          // Listen for new notifications (from backend NotificationService)
          this.hubConnection?.on('NotificationReceived', (data: any) => {
            // Backend sends { notification: dto, unreadCount: number }
            if (data?.notification) {
              this.addNotification(data.notification);
              if (data.unreadCount !== undefined) {
                this.unreadCountSubject.next(data.unreadCount);
              }
            }
          });

          // Listen for subscription confirmation
          this.hubConnection?.on('SubscriptionConfirmed', (data: any) => {
          });

          // Listen for notification read event (from MarkAsRead)
          this.hubConnection?.on('NotificationRead', (data: any) => {
            if (data?.unreadCount !== undefined) {
              this.unreadCountSubject.next(data.unreadCount);
            }
          });

          // Listen for all notifications read event (from MarkAllAsRead)
          this.hubConnection?.on('AllNotificationsRead', (data: any) => {
            this.updateUnreadCount();
          });

          // Listen for errors from server
          this.hubConnection?.on('Error', (error: any) => {
            console.error('SignalR error:', error);
          });
        })
        .catch((err: any) => {
          console.error('SignalR connection error:', err);
          console.error('Error details:', {
            message: err?.message,
            name: err?.name,
            stack: err?.stack
          });
          this.connectionStatusSubject.next(false);
        });
    } catch (error: any) {
      console.error('Error initializing SignalR:', error);
      console.error('Error details:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });
    }
  }

  /**
   * Subscribe user to notifications
   * Calls backend hub method SubscribeToNotifications(userId)
   * Backend validates that user can only subscribe to their own notifications
   */
  private subscribeToNotifications(userId: string): void {
    if (this.hubConnection) {
      this.hubConnection
        .invoke('SubscribeToNotifications', userId)
        .then(() => {
        })
        .catch((err: any) => {
          console.error('Error subscribing to notifications:', err);
          console.error('Backend error details:', err?.message || err);
        });
    } else {
      console.warn('hubConnection is null, cannot subscribe to notifications');
    }
  }

  /**
   * Unsubscribe from notifications
   * Calls backend hub method UnsubscribeFromNotifications(userId)
   */
  public unsubscribeFromNotifications(userId: string): void {
    if (this.hubConnection) {
      this.hubConnection
        .invoke('UnsubscribeFromNotifications', userId)
        .then(() => {
        })
        .catch((err: any) => {
          console.error('Error unsubscribing from notifications:', err);
        });
    }
  }

  /**
   * Add notification to the list
   */
  private addNotification(notification: any): void {
    // Convert backend string enums to frontend numeric enums
    const convertedNotification: NotificationEvent = {
      id: notification.id,
      userId: notification.userId || notification.iamUserId || 0,
      iamUserId: notification.iamUserId || 0,
      type: NotificationEnumConverter.toNotificationType(notification.type),
      resourceName: notification.resourceName,
      resourceType: NotificationEnumConverter.toResourceType(notification.resourceType),
      action: NotificationEnumConverter.toNotificationAction(notification.action),
      permissionType: NotificationEnumConverter.toPermissionType(notification.permissionType),
      metadata: notification.metadata,
      isRead: notification.isRead,
      readAt: notification.readAt,
      createdAt: notification.createdAt,
      updatedAt: notification.updatedAt
    };

    const currentNotifications = this.notificationsSubject.value;
    this.notificationsSubject.next([convertedNotification, ...currentNotifications]);
    this.updateUnreadCount();
  }

  /**
   * Update unread count
   */
  private updateUnreadCount(): void {
    const unreadCount = this.notificationsSubject.value.filter((n) => !n.isRead).length;
    this.unreadCountSubject.next(unreadCount);
  }

  /**
   * Fetch notifications from API
   */
  public getNotifications(pageNumber: number = 1, pageSize: number = 10, type?: string): Observable<NotificationsResponse> {
    return this.ensureUserInitialized().pipe(
      switchMap(iamUserId => {
        let url = `${this.apiUrl}/api/v1/notifications?iamUserId=${iamUserId}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (type) {
          url += `&type=${type}`;
        }

        return this.http.get<NotificationsResponse>(url).pipe(
          tap((response) => {
            if (response.success && response.data.items) {
              // On first page, replace all notifications; on subsequent pages, append
              if (pageNumber === 1) {
                this.notificationsSubject.next(response.data.items);
              } else {
                const currentNotifications = this.notificationsSubject.value;
                this.notificationsSubject.next([...currentNotifications, ...response.data.items]);
              }
              this.updateUnreadCount();
            }
          }),
          catchError((error) => {
            console.error('Error fetching notifications:', error);
            return throwError(() => ({
              message: error?.error?.message || 'Failed to fetch notifications',
              error
            }));
          })
        );
      })
    );
  }

  /**
   * Mark notification as read
   */
  public markAsRead(id: number): Observable<NotificationSingleResponse> {
    return this.ensureUserInitialized().pipe(
      switchMap(iamUserId => {
        return this.http.patch<NotificationSingleResponse>(`${this.apiUrl}/api/v1/notifications/${id}/read?iamUserId=${iamUserId}`, {}).pipe(
          tap(() => {
            const notifications = this.notificationsSubject.value;
            const notification = notifications.find((n) => n.id === id);
            if (notification) {
              notification.isRead = true;
              this.notificationsSubject.next([...notifications]);
              this.updateUnreadCount();
            }
          }),
          catchError(this.handleError('Error marking notification as read'))
        );
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  public markAllAsRead(): Observable<MarkAllAsReadResponse> {
    return this.ensureUserInitialized().pipe(
      switchMap(iamUserId => {
        return this.http.patch<MarkAllAsReadResponse>(`${this.apiUrl}/api/v1/notifications/read-all?iamUserId=${iamUserId}`, {}).pipe(
          tap(() => {
            const notifications = this.notificationsSubject.value;
            notifications.forEach((n) => {
              n.isRead = true;
            });
            this.notificationsSubject.next([...notifications]);
            this.updateUnreadCount();
          }),
          catchError(this.handleError('Error marking all as read'))
        );
      })
    );
  }

  /**
   * Delete notification
   */
  public deleteNotification(id: number): Observable<DeleteNotificationResponse> {
    return this.ensureUserInitialized().pipe(
      switchMap(iamUserId => {
        return this.http.delete<DeleteNotificationResponse>(`${this.apiUrl}/api/v1/notifications/${id}?iamUserId=${iamUserId}`).pipe(
          tap(() => {
            const notifications = this.notificationsSubject.value.filter((n) => n.id !== id);
            this.notificationsSubject.next(notifications);
            this.updateUnreadCount();
          }),
          catchError(this.handleError('Error deleting notification'))
        );
      })
    );
  }

  /**
   * Get unread count
   */
  public getUnreadCount(): Observable<UnreadCountResponse> {
    return this.ensureUserInitialized().pipe(
      switchMap(iamUserId => {
        return this.http.get<UnreadCountResponse>(`${this.apiUrl}/api/v1/notifications/unread-count?iamUserId=${iamUserId}`).pipe(
          tap((response) => {
            if (response.success) {
              this.unreadCountSubject.next(response.data.unreadCount);
            }
          }),
          catchError(this.handleError('Error getting unread count'))
        );
      })
    );
  }

  /**
   * Common error handler for API calls
   * Centralizes error handling and logging
   */
  private handleError(operation: string) {
    return (error: any) => {
      console.error(`${operation} failed:`, error);
      return throwError(() => ({
        message: error?.error?.message || `${operation} failed`,
        error
      }));
    };
  }

  /**
   * Stop SignalR connection
   */
  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection
        .stop()
        .then(() => {
          this.isConnected = false;
          this.connectionStatusSubject.next(false);
          // Clear notifications on disconnect
          this.notificationsSubject.next([]);
          this.unreadCountSubject.next(0);
        })
        .catch((err) => console.error('Error stopping SignalR:', err));
    }

    // Clear cached user information on logout
    this.currentUserId = null;
    this.userEmail = null;
    this.userInitializedSubject.next(false);

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get current notifications
   */
  public getCurrentNotifications(): NotificationEvent[] {
    return this.notificationsSubject.value;
  }

  /**
   * Get connection status
   */
  public isConnectedToHub(): boolean {
    return this.isConnected;
  }
}
